<template>
    <div class="social-sharing">
        <p class="headline">{{ t('resources.share.title') }}</p>
        <div class="button-row">
            <VJoyButton class="social-button link-button" icon="link" size="small" :circle="true" variant="secondary" @click="copyToClipBoard" />
            <a
                target="_blank"
                :href="`https://www.facebook.com/sharer/sharer.php?u=${url}%2F&amp;src=sdkpreparse`"
                class="fb-xfbml-parse-ignore social-button share-button"
            >
                <FacebookIcon />
            </a>
            <a target="_blank" class="social-button share-button" :href="`https://twitter.com/share?url=${url}`"><TwitterIcon /></a>
            <a target="_blank" class="social-button share-button" :href="`https://www.linkedin.com/sharing/share-offsite/?url=${url}`">
                <LinkedinIcon />
            </a>
        </div>
        <div :class="{confirm: true, visible: copiedConfirm}">
            <VJoyHighlight level="success" class="feedback">
                <template #default>{{ t('copy.to.clipboard.message') }}</template>
            </VJoyHighlight>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref, onMounted} from 'vue';
    import {useClipboard} from '@vueuse/core';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyHighlight} from '@maltjoy/core-vue';
    import FacebookIcon from '~/components/icons/Facebook.vue';
    import LinkedinIcon from '~/components/icons/LinkedIn.vue';
    import TwitterIcon from '~/components/icons/Twitter.vue';

    const url = ref('');
    const {t} = useTranslation();
    const {copy} = useClipboard({legacy: true});
    const copiedConfirm = ref(false);

    async function copyToClipBoard() {
        try {
            await copy(url.value);
            copiedConfirm.value = true;
            setTimeout(() => {
                copiedConfirm.value = false;
            }, 2000);
        } catch (_) {}
    }
    onMounted(() => {
        // TO DO : generate server side from host + path
        url.value = window.location.href;
    });
</script>

<style lang="scss">
    .social-sharing {
        padding: var(--joy-core-spacing-6);
        .headline {
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
            margin-bottom: var(--joy-core-spacing-2);
            color: var(--joy-color-neutral-70);
            margin-bottom: var(--joy-core-spacing-4);
        }
        .button-row {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
            .link-button {
                border-width: 1px;
                joy-icon {
                    transform: scale(0.88);
                }
            }
            .share-button {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                border-radius: var(--joy-core-radius-6);
                border: 1px solid var(--joy-color-primary-50);
                transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                &:hover {
                    background-color: var(--joy-color-primary-10);
                }
            }
        }
        .confirm {
            max-height: 0;
            transition: all var(--joy-transition-duration-long) var(--joy-transition-timing-function);
            overflow: hidden;
            .feedback {
                margin-top: var(--joy-core-spacing-4);
            }
            &.visible {
                max-height: 120px;
            }
        }

        .malt-strategy-theme & {
            .button-row {
                .share-button {
                    border: 1px solid var(--joy-color-secondary-50);
                    svg path {
                        fill: var(--joy-color-secondary-50);
                    }
                    &:hover {
                        background-color: var(--joy-color-secondary-10);
                    }
                }
            }
        }
    }

    /* Theme */
    .social-sharing {
        .resource-page & {
            .social-button {
                border: 1px solid var(--joy-color-primary-50);
                color: var(--joy-color-primary-50);
                path {
                    fill: var(--joy-color-primary-50);
                }
                &:hover {
                    background-color: var(--joy-color-primary-10);
                }
            }
        }
        .resource-page--strategy & {
            .social-button {
                border: 1px solid var(--joy-color-secondary-30);
                color: var(--joy-color-secondary-30);
                path {
                    fill: var(--joy-color-secondary-30);
                }
                &:hover {
                    background-color: var(--joy-color-secondary-10);
                }
            }
        }
    }
</style>
